import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { User } from '../../../models/user';
import { useSelection } from '../../../components/context/SelectionContext';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { apiErrorMessage } from '../../inventory/shared/inventoryApi';
import { officeAllowsProductLend } from '../../inventory/shared/PricingModeField';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions } from '../shared/AdminUi';
import RouteFormSections, { EMPTY_ROUTE, RouteFormErrors, RouteFormValues, validateRoute } from './RouteFormSections';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

/** Datos de la ruta que no se muestran en el formulario pero se conservan al guardar. */
interface Hidden {
  startDate: string;
  openingTime: string | null;
  closingTime: string | null;
  enforceSchedule: boolean;
  deviceDetail: boolean;
  allowDeletePayment: boolean;
  allowDeleteLoan: boolean;
}

/** Editar ruta. */
const RouteEdit: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  setPageTitle('routes.editTitle');
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOffice_id, setUser_id } = useSelection();
  const { canUpdate } = useResourcePermissions('/admin/routes');
  const [values, setValues] = useState<RouteFormValues>(EMPTY_ROUTE);
  const [hidden, setHidden] = useState<Hidden | null>(null);
  const [errors, setErrors] = useState<RouteFormErrors>({});
  const [users, setUsers] = useState<User[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [saving, setSaving] = useState(false);
  const productOffice = officeAllowsProductLend(offices, values.officeId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, officesResponse, routesResponse] = await Promise.all([
          axios.get('sales-reps/unassigned'),
          axios.get('offices/all'),
          axios.get(`routes/${id}`),
        ]);
        const route = routesResponse.data?.data?.[0];
        const availableUsers: User[] = Array.isArray(usersResponse.data?.data) ? usersResponse.data.data : [];
        const currentUser = route?.user;
        // El representante actual de la ruta también se puede elegir.
        setUsers(currentUser ? [currentUser, ...availableUsers.filter((user) => user.id !== currentUser.id)] : availableUsers);
        setOffices(Array.isArray(officesResponse.data?.data) ? officesResponse.data.data : []);
        if (!route) return;
        const pad = (s: string) => s.split(':').slice(0, 2).join(':');
        setHidden({
          startDate: route.startDate,
          openingTime: route.openingTime ? pad(route.openingTime) : null,
          closingTime: route.closingTime ? pad(route.closingTime) : null,
          enforceSchedule: Boolean(route.enforceSchedule),
          deviceDetail: Boolean(route.deviceDetail),
          allowDeletePayment: route.allowDeletePayment === true,
          allowDeleteLoan: route.allowDeleteLoan === true,
        });
        setValues({
          name: route.name ?? '',
          officeId: route.office?.id != null ? String(route.office.id) : '',
          userId: route.user?.id != null ? String(route.user.id) : '',
          creditLimit: route.creditLimit != null ? String(route.creditLimit) : '',
          discountDays: String(route.discountDays ?? 0),
          insurance: String(route.insurance ?? 0),
          insuranceRate: String(route.insuranceRate ?? 0),
          manualInsurance: Boolean(route.manualInsurance),
          allowClientCreditLimit: Boolean(Number(route.allowClientCreditLimit ?? 0)),
          blockNewLoanAfterInstallments: route.blockNewLoanAfterInstallments ? String(route.blockNewLoanAfterInstallments) : '',
          productPricingMode: route.productPricingMode === 'PRODUCT' ? 'PRODUCT' : 'CONSTANT',
          requireExpensePhoto: Boolean(Number(route.requireExpensePhoto ?? 0)),
          maxDiscountPerDay: Number(route.maxDiscountPerDay) > 0 ? String(Number(route.maxDiscountPerDay)) : '',
          maxRefinancesPerDay: Number(route.maxRefinancesPerDay) > 0 ? String(route.maxRefinancesPerDay) : '',
          maxExpensesPerDay: Number(route.maxExpensesPerDay) > 0 ? String(Number(route.maxExpensesPerDay)) : '',
        });
        // Las demás pantallas recuerdan la sede y el representante elegidos.
        if (route.user) setUser_id(route.user.id);
        if (route.office) setOffice_id(route.office.id);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!canUpdate) return;
    const found = validateRoute(values, t('common.required'));
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('routes.missingFields'));
      return;
    }
    const v = values;
    const h = hidden;
    setSaving(true);
    try {
      await axios.put(`routes/${id}`, {
        name: v.name,
        officeId: Number(v.officeId),
        userId: Number(v.userId),
        creditLimit: v.creditLimit,
        discountDays: v.discountDays,
        insurance: v.insurance,
        insuranceRate: v.insuranceRate,
        manualInsurance: v.manualInsurance,
        startDate: h?.startDate,
        openingTime: h?.enforceSchedule ? h.openingTime ?? '00:00' : null,
        closingTime: h?.enforceSchedule ? h.closingTime ?? '00:00' : null,
        enforceSchedule: h?.enforceSchedule ?? false,
        deviceDetail: h?.deviceDetail ?? false,
        allowDeletePayment: h?.allowDeletePayment ?? true,
        allowDeleteLoan: h?.allowDeleteLoan ?? true,
        allowClientCreditLimit: v.allowClientCreditLimit,
        blockNewLoanAfterInstallments: Number(v.blockNewLoanAfterInstallments) > 0 ? Number(v.blockNewLoanAfterInstallments) : null,
        requireExpensePhoto: v.requireExpensePhoto,
        maxDiscountPerDay: Number(v.maxDiscountPerDay) > 0 ? Number(v.maxDiscountPerDay) : null,
        maxRefinancesPerDay: Number(v.maxRefinancesPerDay) > 0 ? Number(v.maxRefinancesPerDay) : null,
        maxExpensesPerDay: Number(v.maxExpensesPerDay) > 0 ? Number(v.maxExpensesPerDay) : null,
        productPricingMode: productOffice ? v.productPricingMode : 'CONSTANT',
      });
      notify.success(t('common.savedOk'));
      navigate('/admin/routes');
    } catch (error) {
      notify.error(apiErrorMessage(error, t('routes.updateError')));
    } finally {
      setSaving(false);
    }
  };

  const title = t('routes.editTitle');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={values.name || undefined}
        breadcrumbs={[{ label: t('routes.title'), href: '#/admin/routes' }, { label: title }]}
        helpAction={<ScreenTourButton />}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <RouteFormSections
          values={values}
          errors={errors}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          offices={offices}
          users={users}
          productOffice={productOffice}
        />
        <FormActions cancelTo="/admin/routes" saving={saving} allowed={canUpdate} deniedTooltip={t('common.noPermissionEdit')} />
      </Box>
    </AdminPage>
  );
};

export default RouteEdit;
